import React from "react";
import { DEFAULT_GEOMETRY } from "../../artLabels/artLabelPreviewMath";
import { SLAB_MM, slabPreviewLayout } from "./layout/slabMm";

const GLARE =
  "linear-gradient(112deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.10) 28%, rgba(255,255,255,0) 46%, rgba(255,255,255,0) 68%, rgba(255,255,255,0.16) 100%)";

/**
 * Simulação do slab CGC (spec 2026-10-01 R3), compartilhada pelo gerador de etiquetas e pela
 * revisão da arte. Pura: tudo sai de `geometry` (GET /admin/art-labels/geometry) e da escala.
 * - `width` (px do corpo) ou `pxPerMm`: `width` vence e deixa o slab responsivo.
 * - `faceSize`: tamanho da frente da etiqueta nas unidades de `children`; o conteúdo é escalado
 *   para a janela (labelWidth × frontHeight) e o que passa dela fica escondido, como no holder.
 * - `cardImageUrl`: carta na posição física (ancorada no fundo); sem imagem, um marcador.
 * - `guides`: sangria (fora da janela) e área segura (dentro). `glare`: reflexo do acrílico.
 */
const SlabMock = ({ geometry, pxPerMm = 4, width = null, faceSize, cardImageUrl = null, guides = false, glare = true, children = null }) => {
  const g = { ...DEFAULT_GEOMETRY, ...(geometry || {}) };
  const S = { ...SLAB_MM, ...(g.slab || {}) };
  const unit = width ? width / S.width : pxPerMm;
  const mm = (v) => v * unit;
  const L = slabPreviewLayout(g, unit);
  const face = faceSize || { width: L.label.width, height: L.label.height };
  const sx = L.label.width / face.width;
  const sy = L.label.height / face.height;
  const cardBox = {
    position: "absolute",
    left: L.card.left,
    top: L.card.top,
    width: L.card.width,
    height: L.card.height,
    borderRadius: unit * 2.2,
  };
  const guideBox = (left, top, w, h, color, testId) => (
    <div
      data-testid={testId}
      aria-hidden="true"
      style={{ position: "absolute", left, top, width: w, height: h, border: `1px dashed ${color}`, boxSizing: "border-box", pointerEvents: "none" }}
    />
  );
  return (
    <div
      data-testid="slab-preview"
      style={{
        width: L.width,
        height: L.height,
        position: "relative",
        flexShrink: 0,
        borderRadius: L.radius,
        border: `${Math.max(1, unit * 0.25)}px solid #9aa0a8`,
        // acrílico: gradiente frio com borda clara e sombra projetada
        background: "linear-gradient(160deg, #f8f8fa 0%, #dfe1e6 45%, #eff1f4 100%)",
        boxShadow: `0 ${unit * 1.5}px ${unit * 4}px rgba(0,0,0,0.35), inset 0 0 0 ${unit * 0.6}px rgba(255,255,255,0.7), inset 0 0 0 ${unit * 0.8}px rgba(0,0,0,0.06)`,
        overflow: "hidden",
      }}
    >
      {/* poço rebaixado que abriga o inserto e a carta */}
      <div
        data-testid="slab-well"
        style={{
          position: "absolute",
          left: L.well.left,
          top: L.well.top,
          width: L.well.width,
          height: L.well.height,
          borderRadius: unit * 2,
          background: "linear-gradient(180deg, #f3f4f6 0%, #e4e6ea 100%)",
          boxShadow: `inset 0 ${unit * 0.4}px ${unit * 1.6}px rgba(0,0,0,0.38), inset 0 -${unit * 0.2}px ${unit * 0.4}px rgba(255,255,255,0.7)`,
        }}
      />
      <div
        data-testid="slab-label-window"
        style={{
          position: "absolute",
          left: L.label.left,
          top: L.label.top,
          width: L.label.width,
          height: L.label.height,
          overflow: "hidden",
          boxShadow: `0 0 0 1px rgba(0,0,0,0.55), 0 ${unit * 0.3}px ${unit * 0.8}px rgba(0,0,0,0.25)`,
          backgroundColor: "#ffffff",
        }}
      >
        <div
          data-testid="slab-label-content"
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: face.width,
            height: face.height,
            transform: `scale(${sx}, ${sy})`,
            transformOrigin: "top left",
          }}
        >
          {children}
        </div>
      </div>
      {cardImageUrl ? (
        <img
          alt=""
          data-testid="slab-card"
          src={cardImageUrl}
          draggable={false}
          style={{
            ...cardBox,
            objectFit: "fill",
            // anula o preflight do Tailwind (img { max-width: 100%; height: auto })
            maxWidth: "none",
            boxShadow: `0 ${unit * 0.3}px ${unit * 1}px rgba(0,0,0,0.45)`,
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      ) : (
        <div
          data-testid="slab-card"
          style={{
            ...cardBox,
            border: "1px dashed #9a9a9a",
            color: "#7a7a7a",
            fontSize: 11,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          carta (imagem indisponível)
        </div>
      )}
      {guides ? (
        <>
          {guideBox(L.label.left - mm(g.bleed), L.label.top - mm(g.bleed), L.label.width + 2 * mm(g.bleed), L.label.height + 2 * mm(g.bleed), "#e11d48", "slab-guide-bleed")}
          {guideBox(L.label.left + mm(g.safeInset), L.label.top + mm(g.safeInset), L.label.width - 2 * mm(g.safeInset), L.label.height - 2 * mm(g.safeInset), "#16a34a", "slab-guide-safe")}
        </>
      ) : null}
      {/* reflexo do acrílico por cima de tudo */}
      {glare ? (
        <div
          data-testid="slab-glare"
          aria-hidden="true"
          style={{ position: "absolute", inset: 0, borderRadius: L.radius, background: GLARE, pointerEvents: "none" }}
        />
      ) : null}
    </div>
  );
};

export default SlabMock;
